import React, { useState, useEffect, useRef } from "react";
import { playlistsAPI, supabase, normalizeText } from "../services/supabaseClient";

const TABS = {
  ADD: "add",
  IMPORT: "import",
  SMART: "smart",
  ARTIST: "artist",
  CHECK: "check",
};

const SqlGenerator = () => {
  const [activeTab, setActiveTab] = useState(TABS.ADD);

  // Add Song state
  const [urlInput, setUrlInput] = useState("");
  const [videoId, setVideoId] = useState("");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [title, setTitle] = useState("");
  const [sqlOutput, setSqlOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const [artists, setArtists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [artistSearch, setArtistSearch] = useState("");

  // Video checker state
  const [isChecking, setIsChecking] = useState(false);
  const [checkProgress, setCheckProgress] = useState({ current: 0, total: 0 });
  const [brokenVideos, setBrokenVideos] = useState([]);
  const [checkComplete, setCheckComplete] = useState(false);
  const [copiedDeleteId, setCopiedDeleteId] = useState(null);

  // Add Artist state
  const [newArtistName, setNewArtistName] = useState("");
  const [newArtistLogo, setNewArtistLogo] = useState("");
  const [newArtistWww, setNewArtistWww] = useState("");
  const [newArtistFb, setNewArtistFb] = useState("");
  const [newArtistYt, setNewArtistYt] = useState("");
  const [artistSqlOutput, setArtistSqlOutput] = useState("");
  const [artistCopied, setArtistCopied] = useState(false);

  // Bulk Import state
  const [importVideos, setImportVideos] = useState([]);
  const [isFetchingTitles, setIsFetchingTitles] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 });
  const [importArtist, setImportArtist] = useState(null);
  const [importSqlOutput, setImportSqlOutput] = useState("");
  const [importCopied, setImportCopied] = useState(false);
  const [importArtistSearch, setImportArtistSearch] = useState("");
  const fileInputRef = useRef(null);

  // Smart Import state
  const [smartInput, setSmartInput] = useState("");
  const [smartVideos, setSmartVideos] = useState([]);
  const [isSmartProcessing, setIsSmartProcessing] = useState(false);
  const [smartProgress, setSmartProgress] = useState({ current: 0, total: 0 });
  const [smartSqlOutput, setSmartSqlOutput] = useState("");
  const [smartCopied, setSmartCopied] = useState(false);
  const [smartRemoveStr, setSmartRemoveStr] = useState("");

  const titleRef = useRef(null);

  // Fetch artists from database on mount
  useEffect(() => {
    const fetchArtists = async () => {
      try {
        setIsLoading(true);
        const data = await playlistsAPI.getAll();
        setArtists(data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch artists:", err);
        setError("Nepodařilo se načíst interprety z databáze");
      } finally {
        setIsLoading(false);
      }
    };
    fetchArtists();
  }, []);

  // Auto-focus title after video ID is extracted
  useEffect(() => {
    if (videoId && titleRef.current && !title) {
      titleRef.current.focus();
    }
  }, [videoId, title]);

  const extractVideoId = (url) => {
    setUrlInput(url);
    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/
    );
    if (match) {
      setVideoId(match[1]);
    } else if (url.length >= 8 && !url.includes(" ")) {
      setVideoId(url);
    } else {
      setVideoId("");
    }
  };

  const handleArtistSelect = (artist) => {
    setSelectedArtist(artist);
  };

  const filteredArtists = artists.filter((a) =>
    a.name.toLowerCase().includes(artistSearch.toLowerCase())
  );

  const getArtistImage = (logo) => {
    try {
      return require(`../assets/playlists/${logo}`);
    } catch {
      return null;
    }
  };

  const generateSql = () => {
    if (!videoId || !selectedArtist || !title) return;

    const sql = `INSERT INTO videos (video_id, artist, title, playlist_id)\nVALUES ('${videoId}', '${selectedArtist.name.replace(/'/g, "''")}', '${title.replace(/'/g, "''")}', ${selectedArtist.id});`;

    setSqlOutput(sql);
    setCopied(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(sqlOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setUrlInput("");
    setVideoId("");
    setTitle("");
    setSqlOutput("");
    setCopied(false);
  };

  const isFormValid = videoId && selectedArtist && title;

  // --- Video Checker ---
  const checkVideoValidity = async (vid) => {
    try {
      const response = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${vid}&format=json`
      );
      return response.ok;
    } catch {
      return false;
    }
  };

  const checkAllVideos = async () => {
    setIsChecking(true);
    setBrokenVideos([]);
    setCheckComplete(false);

    try {
      const { data: videos, error: fetchError } = await supabase
        .from("videos")
        .select("*, playlists:playlist_id(name)");

      if (fetchError) throw fetchError;

      setCheckProgress({ current: 0, total: videos.length });
      const broken = [];

      for (let i = 0; i < videos.length; i++) {
        const video = videos[i];
        const isValid = await checkVideoValidity(video.video_id);

        if (!isValid) {
          broken.push({
            ...video,
            artistName: video.playlists?.name || video.artist,
          });
        }

        setCheckProgress({ current: i + 1, total: videos.length });
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setBrokenVideos(broken);
      setCheckComplete(true);
    } catch (err) {
      console.error("Error checking videos:", err);
      setError("Nepodařilo se zkontrolovat videa");
    } finally {
      setIsChecking(false);
    }
  };

  const copyDeleteSql = (video) => {
    const sql = `DELETE FROM videos WHERE id = ${video.id}; -- ${video.artistName}: ${video.title}`;
    navigator.clipboard.writeText(sql);
    setCopiedDeleteId(video.id);
    setTimeout(() => setCopiedDeleteId(null), 2000);
  };

  const progressPercent =
    checkProgress.total > 0
      ? Math.round((checkProgress.current / checkProgress.total) * 100)
      : 0;

  // --- Add Artist ---
  const generateArtistSql = () => {
    if (!newArtistName || !newArtistLogo) return;

    const esc = (s) => s.replace(/'/g, "''");
    const wwwVal = newArtistWww ? `'${esc(newArtistWww)}'` : "''";
    const fbVal = newArtistFb ? `'${esc(newArtistFb)}'` : "''";
    const ytVal = newArtistYt ? `'${esc(newArtistYt)}'` : "''";

    const sql = `INSERT INTO playlists (name, logo, www_link, fb_link, yt_link)\nVALUES ('${esc(newArtistName)}', '${esc(newArtistLogo)}', ${wwwVal}, ${fbVal}, ${ytVal});`;

    setArtistSqlOutput(sql);
    setArtistCopied(false);
  };

  const copyArtistSql = () => {
    navigator.clipboard.writeText(artistSqlOutput);
    setArtistCopied(true);
    setTimeout(() => setArtistCopied(false), 2000);
  };

  const resetArtistForm = () => {
    setNewArtistName("");
    setNewArtistLogo("");
    setNewArtistWww("");
    setNewArtistFb("");
    setNewArtistYt("");
    setArtistSqlOutput("");
    setArtistCopied(false);
  };

  const isArtistFormValid = newArtistName && newArtistLogo;

  // --- Bulk Import ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);

    const videos = lines.map((line) => {
      const match = line.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/
      );
      const vid = match ? match[1] : line;
      return { videoId: vid, title: "", status: "pending" };
    });

    setImportVideos(videos);
    setImportSqlOutput("");
    setImportCopied(false);

    // Fetch titles
    setIsFetchingTitles(true);
    setFetchProgress({ current: 0, total: videos.length });

    const updated = [...videos];
    for (let i = 0; i < updated.length; i++) {
      try {
        const res = await fetch(
          `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${updated[i].videoId}&format=json`
        );
        if (res.ok) {
          const data = await res.json();
          updated[i] = { ...updated[i], title: data.title, status: "ok" };
        } else {
          updated[i] = { ...updated[i], title: "(nedostupné video)", status: "error" };
        }
      } catch {
        updated[i] = { ...updated[i], title: "(chyba načítání)", status: "error" };
      }
      setFetchProgress({ current: i + 1, total: updated.length });
      setImportVideos([...updated]);
      await new Promise((r) => setTimeout(r, 100));
    }

    setIsFetchingTitles(false);
    // Reset file input so same file can be re-uploaded
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImportVideo = (index) => {
    setImportVideos((prev) => prev.filter((_, i) => i !== index));
    setImportSqlOutput("");
  };

  const updateImportTitle = (index, newTitle) => {
    setImportVideos((prev) =>
      prev.map((v, i) => (i === index ? { ...v, title: newTitle } : v))
    );
    setImportSqlOutput("");
  };

  const filteredImportArtists = artists.filter((a) =>
    a.name.toLowerCase().includes(importArtistSearch.toLowerCase())
  );

  const generateImportSql = () => {
    if (!importArtist || importVideos.length === 0) return;

    const esc = (s) => s.replace(/'/g, "''");
    const statements = importVideos.map(
      (v) =>
        `INSERT INTO videos (video_id, artist, title, playlist_id)\nVALUES ('${v.videoId}', '${esc(importArtist.name)}', '${esc(v.title)}', ${importArtist.id});`
    );

    setImportSqlOutput(statements.join("\n\n"));
    setImportCopied(false);
  };

  const copyImportSql = () => {
    navigator.clipboard.writeText(importSqlOutput);
    setImportCopied(true);
    setTimeout(() => setImportCopied(false), 2000);
  };

  const isImportReady = importArtist && importVideos.length > 0;

  // --- Smart Import ---
  const channelArtistMapRef = useRef({});

  const processSmartLinks = async () => {
    if (!smartInput.trim()) return;

    const lines = smartInput.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
    const extractedIds = lines.map((line) => {
      const match = line.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
      return match ? match[1] : line;
    }).filter((id) => id.length >= 8); // rough filter

    if (extractedIds.length === 0) return;

    const uniqueIds = [...new Set(extractedIds)];

    setIsSmartProcessing(true);
    setSmartProgress({ current: 0, total: uniqueIds.length });
    setSmartSqlOutput("");

    let existingVideosSet = new Set();
    let representativeVideos = [];
    try {
      const { data } = await supabase.from("videos").select("video_id, playlist_id");
      if (data) {
        existingVideosSet = new Set(data.map((v) => v.video_id));
        const seen = new Set();
        for (const v of data) {
          if (v.playlist_id && !seen.has(v.playlist_id)) {
            seen.add(v.playlist_id);
            representativeVideos.push(v);
          }
        }
      }
    } catch (e) {
      console.error("Failed to fetch existing videos", e);
    }

    // Build channel -> artist map from DB if empty
    if (Object.keys(channelArtistMapRef.current).length === 0 && representativeVideos.length > 0) {
      const chunkSize = 10;
      for (let i = 0; i < representativeVideos.length; i += chunkSize) {
        const chunk = representativeVideos.slice(i, i + chunkSize);
        await Promise.all(chunk.map(async (v) => {
          try {
            const res = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${v.video_id}&format=json`);
            if (res.ok) {
              const data = await res.json();
              if (data.author_name) {
                channelArtistMapRef.current[normalizeText(data.author_name)] = v.playlist_id;
              }
            }
          } catch (e) {}
        }));
      }
    }

    const results = [];
    for (let i = 0; i < uniqueIds.length; i++) {
      const vid = uniqueIds[i];
      if (existingVideosSet.has(vid)) {
        results.push({ videoId: vid, title: "Již v databázi", status: "exists" });
      } else {
        try {
          const res = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${vid}&format=json`);
          if (res.ok) {
            const data = await res.json();
            const channelName = data.author_name || "";
            const normalizedChannel = normalizeText(channelName);
            
            let suggestedId = channelArtistMapRef.current[normalizedChannel] || "";

            // Fallback fuzzy match
            if (!suggestedId) {
              let suggested = artists.find((a) => {
                const na = normalizeText(a.name);
                return na === normalizedChannel || normalizedChannel.includes(na) || na.includes(normalizedChannel);
              });
              if (suggested) suggestedId = suggested.id;
            }

            // Fallback current batch match
            if (!suggestedId) {
              const prev = results.find(r => r.status === "ok" && normalizeText(r.channelName) === normalizedChannel && r.selectedArtistId);
              if (prev) suggestedId = prev.selectedArtistId;
            }

            results.push({
              videoId: vid,
              title: data.title,
              status: "ok",
              channelName,
              selectedArtistId: suggestedId
            });
          } else {
            results.push({ videoId: vid, title: "Nedostupné video", status: "error" });
          }
        } catch {
          results.push({ videoId: vid, title: "Chyba načítání", status: "error" });
        }
      }
      setSmartProgress({ current: i + 1, total: uniqueIds.length });
      setSmartVideos([...results]);
      await new Promise((r) => setTimeout(r, 100));
    }

    setIsSmartProcessing(false);
  };


  const updateSmartVideoArtist = (index, newArtistId) => {
    setSmartVideos((prev) => prev.map((v, i) => (i === index ? { ...v, selectedArtistId: newArtistId } : v)));
    setSmartSqlOutput("");
  };

  const updateSmartVideoTitle = (index, newTitle) => {
    setSmartVideos((prev) => prev.map((v, i) => (i === index ? { ...v, title: newTitle } : v)));
    setSmartSqlOutput("");
  };

  const removeSmartVideo = (index) => {
    setSmartVideos((prev) => prev.filter((_, i) => i !== index));
    setSmartSqlOutput("");
  };

  const applySmartRemoveString = () => {
    if (!smartRemoveStr) return;
    setSmartVideos((prev) => prev.map((v) => ({
      ...v,
      title: v.title.replace(new RegExp(smartRemoveStr, 'gi'), '').trim()
    })));
    setSmartSqlOutput("");
  };

  const generateSmartSql = () => {
    const validVideos = smartVideos.filter((v) => v.status === "ok" && v.selectedArtistId);
    if (validVideos.length === 0) return;

    const esc = (s) => s.replace(/'/g, "''");
    const statements = validVideos.map((v) => {
      const artistObj = artists.find((a) => a.id.toString() === v.selectedArtistId.toString());
      if (!artistObj) return "";
      return `INSERT INTO videos (video_id, artist, title, playlist_id)\nVALUES ('${v.videoId}', '${esc(artistObj.name)}', '${esc(v.title)}', ${artistObj.id});`;
    }).filter((s) => s !== "");

    setSmartSqlOutput(statements.join("\n\n"));
    setSmartCopied(false);
  };

  const copySmartSql = () => {
    navigator.clipboard.writeText(smartSqlOutput);
    setSmartCopied(true);
    setTimeout(() => setSmartCopied(false), 2000);
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>⚙️</div>
          <h1 style={styles.headerTitle}>Administrace</h1>
          <p style={styles.headerSub}>Zvuky Víry · Nástroje databáze</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            onClick={() => setActiveTab(TABS.ADD)}
            style={{
              ...styles.tab,
              ...(activeTab === TABS.ADD ? styles.tabActive : {}),
            }}
          >
            ＋ Píseň
          </button>
          <button
            onClick={() => setActiveTab(TABS.IMPORT)}
            style={{
              ...styles.tab,
              ...(activeTab === TABS.IMPORT ? styles.tabActive : {}),
            }}
          >
            📥 Import
          </button>
          <button
            onClick={() => setActiveTab(TABS.SMART)}
            style={{
              ...styles.tab,
              ...(activeTab === TABS.SMART ? styles.tabActive : {}),
            }}
          >
            🧠 Parser
          </button>
          <button
            onClick={() => setActiveTab(TABS.ARTIST)}
            style={{
              ...styles.tab,
              ...(activeTab === TABS.ARTIST ? styles.tabActive : {}),
            }}
          >
            ＋ Interpret
          </button>
          <button
            onClick={() => setActiveTab(TABS.CHECK)}
            style={{
              ...styles.tab,
              ...(activeTab === TABS.CHECK ? styles.tabActive : {}),
            }}
          >
            🔍 Kontrola
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div style={styles.errorBanner}>
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)} style={styles.errorClose}>
              ✕
            </button>
          </div>
        )}

        {/* ===== ADD SONG TAB ===== */}
        {activeTab === TABS.ADD && (
          <div style={styles.card}>
            {/* Step 1: Artist */}
            <div style={styles.step}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNumber}>1</span>
                <span style={styles.stepLabel}>Vyberte interpreta</span>
              </div>
              {isLoading ? (
                <div style={styles.loadingPulse}>Načítání interpretů…</div>
              ) : (
                <>
                  <input
                    type="text"
                    value={artistSearch}
                    onChange={(e) => setArtistSearch(e.target.value)}
                    placeholder="Hledat interpreta…"
                    style={styles.input}
                  />
                  <div style={styles.artistGrid}>
                    {filteredArtists.map((a) => {
                      const isSelected = selectedArtist?.id === a.id;
                      const imgSrc = getArtistImage(a.logo);
                      return (
                        <div
                          key={a.id}
                          onClick={() => handleArtistSelect(a)}
                          style={{
                            ...styles.artistCard,
                            ...(isSelected ? styles.artistCardSelected : {}),
                          }}
                        >
                          {imgSrc && (
                            <img
                              src={imgSrc}
                              alt={a.name}
                              style={styles.artistImg}
                            />
                          )}
                          <div style={styles.artistName}>{a.name}</div>
                          <div style={{ fontSize: "0.65rem", color: "#888", marginTop: 2 }}>{a.videoCount || 0} videí</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {selectedArtist && (
                <div style={styles.badge}>
                  ✓ {selectedArtist.name} · playlist_id = {selectedArtist.id}
                </div>
              )}
            </div>

            {/* Step 2: YouTube Link */}
            <div style={styles.step}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNumber}>2</span>
                <span style={styles.stepLabel}>Vložte YouTube odkaz</span>
              </div>
              <input
                type="text"
                value={urlInput}
                onChange={(e) => extractVideoId(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                style={styles.input}
              />
              {videoId && (
                <div style={styles.badge}>
                  ✓ video_id = {videoId}
                </div>
              )}
            </div>

            {/* Step 3: Title */}
            <div style={styles.step}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNumber}>3</span>
                <span style={styles.stepLabel}>Název písně</span>
              </div>
              <input
                ref={titleRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="např. Nová Píseň"
                style={styles.input}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isFormValid) generateSql();
                }}
              />
            </div>

            {/* Generate Button */}
            <button
              onClick={generateSql}
              style={{
                ...styles.primaryBtn,
                ...(isFormValid ? {} : styles.primaryBtnDisabled),
              }}
              disabled={!isFormValid}
            >
              Generovat SQL
            </button>

            {/* SQL Output */}
            {sqlOutput && (
              <div style={styles.sqlOutputCard}>
                <pre style={styles.sqlCode}>{sqlOutput}</pre>
                <div style={styles.sqlActions}>
                  <button onClick={copyToClipboard} style={styles.copyBtn}>
                    {copied ? "✓ Zkopírováno!" : "📋 Kopírovat"}
                  </button>
                  <button onClick={resetForm} style={styles.resetBtn}>
                    ↻ Nový
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== LINK CHECKER TAB ===== */}
        {activeTab === TABS.CHECK && (
          <div style={styles.card}>
            <p style={styles.cardDescription}>
              Projde všechna videa v databázi a zkontroluje, zda jsou jejich
              YouTube odkazy stále platné.
            </p>

            <button
              onClick={checkAllVideos}
              style={{
                ...styles.primaryBtn,
                ...(isChecking ? styles.primaryBtnDisabled : {}),
                backgroundColor: isChecking ? "#b0a8ef" : "#6c5ce7",
              }}
              disabled={isChecking}
            >
              {isChecking ? "Kontroluji…" : "🔍 Spustit kontrolu"}
            </button>

            {/* Progress */}
            {isChecking && (
              <div style={styles.progressSection}>
                <div style={styles.progressTrack}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${progressPercent}%`,
                    }}
                  />
                </div>
                <div style={styles.progressText}>
                  {checkProgress.current} / {checkProgress.total} videí ·{" "}
                  {progressPercent}%
                </div>
              </div>
            )}

            {/* Results */}
            {checkComplete && (
              <div style={styles.results}>
                {brokenVideos.length === 0 ? (
                  <div style={styles.successBanner}>
                    <span style={styles.successIcon}>✅</span>
                    <div>
                      <strong>Vše v pořádku!</strong>
                      <br />
                      Všech {checkProgress.total} videí je platných.
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.warningBanner}>
                      Nalezeno <strong>{brokenVideos.length}</strong> nefunkčních
                      videí z {checkProgress.total}
                    </div>
                    <div style={styles.brokenList}>
                      {brokenVideos.map((video) => (
                        <div key={video.id} style={styles.brokenCard}>
                          <div style={styles.brokenMain}>
                            <div style={styles.brokenArtist}>
                              {video.artistName}
                            </div>
                            <div style={styles.brokenTitle}>{video.title}</div>
                            <div style={styles.brokenVideoId}>
                              {video.video_id}
                            </div>
                          </div>
                          <div style={styles.brokenActions}>
                            <a
                              href={`https://youtube.com/watch?v=${video.video_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.openLink}
                            >
                              ↗ Otevřít
                            </a>
                            <button
                              onClick={() => copyDeleteSql(video)}
                              style={styles.deleteCopyBtn}
                            >
                              {copiedDeleteId === video.id
                                ? "✓ Zkopírováno"
                                : "📋 DELETE SQL"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== ADD ARTIST TAB ===== */}
        {activeTab === TABS.ARTIST && (
          <div style={styles.card}>
            <div style={styles.step}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNumber}>1</span>
                <span style={styles.stepLabel}>Název interpreta</span>
              </div>
              <input
                type="text"
                value={newArtistName}
                onChange={(e) => setNewArtistName(e.target.value)}
                placeholder="např. Adorare"
                style={styles.input}
              />
            </div>

            <div style={styles.step}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNumber}>2</span>
                <span style={styles.stepLabel}>Název souboru loga</span>
              </div>
              <input
                type="text"
                value={newArtistLogo}
                onChange={(e) => setNewArtistLogo(e.target.value)}
                placeholder="např. adorare.jpeg"
                style={styles.input}
              />
              <div style={{ ...styles.badge, marginTop: "0.4rem" }}>
                Obrázek vložte do src/assets/playlists/
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNumber}>3</span>
                <span style={styles.stepLabel}>Odkazy (volitelné)</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input
                  type="text"
                  value={newArtistWww}
                  onChange={(e) => setNewArtistWww(e.target.value)}
                  placeholder="Webová stránka"
                  style={styles.input}
                />
                <input
                  type="text"
                  value={newArtistFb}
                  onChange={(e) => setNewArtistFb(e.target.value)}
                  placeholder="Facebook URL"
                  style={styles.input}
                />
                <input
                  type="text"
                  value={newArtistYt}
                  onChange={(e) => setNewArtistYt(e.target.value)}
                  placeholder="YouTube URL"
                  style={styles.input}
                />
              </div>
            </div>

            <button
              onClick={generateArtistSql}
              style={{
                ...styles.primaryBtn,
                ...(isArtistFormValid ? {} : styles.primaryBtnDisabled),
              }}
              disabled={!isArtistFormValid}
            >
              Generovat SQL
            </button>

            {artistSqlOutput && (
              <div style={styles.sqlOutputCard}>
                <pre style={styles.sqlCode}>{artistSqlOutput}</pre>
                <div style={styles.sqlActions}>
                  <button onClick={copyArtistSql} style={styles.copyBtn}>
                    {artistCopied ? "✓ Zkopírováno!" : "📋 Kopírovat"}
                  </button>
                  <button onClick={resetArtistForm} style={styles.resetBtn}>
                    ↻ Nový
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== BULK IMPORT TAB ===== */}
        {activeTab === TABS.IMPORT && (
          <div style={styles.card}>
            <p style={styles.cardDescription}>
              Nahrajte .txt soubor s YouTube odkazy (každý na novém řádku).
              Názvy videí se automaticky načtou.
            </p>

            {/* File Upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                ...styles.primaryBtn,
                ...(isFetchingTitles ? styles.primaryBtnDisabled : {}),
              }}
              disabled={isFetchingTitles}
            >
              {isFetchingTitles
                ? `Načítání... ${fetchProgress.current}/${fetchProgress.total}`
                : "📂 Nahrát soubor (.txt)"}
            </button>

            {/* Fetch Progress */}
            {isFetchingTitles && (
              <div style={styles.progressSection}>
                <div style={styles.progressTrack}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${fetchProgress.total > 0 ? Math.round((fetchProgress.current / fetchProgress.total) * 100) : 0}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Video List */}
            {importVideos.length > 0 && (
              <div style={{ marginTop: "1.25rem" }}>
                <div style={styles.importCount}>
                  {importVideos.length} videí načteno
                </div>
                <div style={styles.importList}>
                  {importVideos.map((video, index) => (
                    <div key={index} style={styles.importItem}>
                      <div style={styles.importItemMain}>
                        <input
                          type="text"
                          value={video.title}
                          onChange={(e) => updateImportTitle(index, e.target.value)}
                          style={{
                            ...styles.importTitleInput,
                            ...(video.status === "error" ? { color: "#dc2626" } : {}),
                          }}
                        />
                        <div style={styles.importVideoId}>{video.videoId}</div>
                      </div>
                      <button
                        onClick={() => removeImportVideo(index)}
                        style={styles.importDeleteBtn}
                        title="Odebrat"
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </div>

                {/* Artist Selection for Import */}
                <div style={{ marginTop: "1.5rem" }}>
                  <div style={styles.stepHeader}>
                    <span style={styles.stepNumber}>▶</span>
                    <span style={styles.stepLabel}>Vyberte interpreta</span>
                  </div>
                  <input
                    type="text"
                    value={importArtistSearch}
                    onChange={(e) => setImportArtistSearch(e.target.value)}
                    placeholder="Hledat interpreta…"
                    style={styles.input}
                  />
                  <div style={styles.artistGrid}>
                    {filteredImportArtists.map((a) => {
                      const isSelected = importArtist?.id === a.id;
                      const imgSrc = getArtistImage(a.logo);
                      return (
                        <div
                          key={a.id}
                          onClick={() => setImportArtist(a)}
                          style={{
                            ...styles.artistCard,
                            ...(isSelected ? styles.artistCardSelected : {}),
                          }}
                        >
                          {imgSrc && (
                            <img
                              src={imgSrc}
                              alt={a.name}
                              style={styles.artistImg}
                            />
                          )}
                          <div style={styles.artistName}>{a.name}</div>
                          <div style={{ fontSize: "0.65rem", color: "#888", marginTop: 2 }}>{a.videoCount || 0} videí</div>
                        </div>
                      );
                    })}
                  </div>
                  {importArtist && (
                    <div style={styles.badge}>
                      ✓ {importArtist.name} · playlist_id = {importArtist.id}
                    </div>
                  )}
                </div>

                {/* Generate Bulk SQL */}
                <button
                  onClick={generateImportSql}
                  style={{
                    ...styles.primaryBtn,
                    marginTop: "1rem",
                    ...(isImportReady ? {} : styles.primaryBtnDisabled),
                  }}
                  disabled={!isImportReady}
                >
                  Generovat SQL ({importVideos.length} videí)
                </button>

                {importSqlOutput && (
                  <div style={styles.sqlOutputCard}>
                    <pre style={styles.sqlCode}>{importSqlOutput}</pre>
                    <div style={styles.sqlActions}>
                      <button onClick={copyImportSql} style={styles.copyBtn}>
                        {importCopied ? "✓ Zkopírováno!" : "📋 Kopírovat"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== SMART IMPORT TAB ===== */}
        {activeTab === TABS.SMART && (
          <div style={styles.card}>
            <p style={styles.cardDescription}>
              Nahrajte více YouTube odkazů (každý na nový řádek). Systém zkontroluje, zda již v databázi jsou 
              a pomůže vám automaticky navrhnout interpreta podle názvu YouTube kanálu.
            </p>

            <textarea
              style={{ ...styles.input, minHeight: 120, resize: "vertical", fontFamily: "monospace", fontSize: "0.8rem" }}
              placeholder={`https://youtube.com/watch?v=...\\nhttps://youtu.be/...`}
              value={smartInput}
              onChange={(e) => setSmartInput(e.target.value)}
            />

            <button
              onClick={processSmartLinks}
              style={{
                ...styles.primaryBtn,
                marginTop: "1rem",
                ...(isSmartProcessing || !smartInput.trim() ? styles.primaryBtnDisabled : {}),
              }}
              disabled={isSmartProcessing || !smartInput.trim()}
            >
              {isSmartProcessing 
                ? `Zpracovávám... ${smartProgress.current}/${smartProgress.total}`
                : "⚙️ Analyzovat videa"}
            </button>

            {isSmartProcessing && (
              <div style={styles.progressSection}>
                <div style={styles.progressTrack}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${smartProgress.total > 0 ? Math.round((smartProgress.current / smartProgress.total) * 100) : 0}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {smartVideos.length > 0 && (
              <div style={{ marginTop: "1.5rem" }}>
                <div style={styles.importCount}>
                  Výsledky analýzy: {" "}
                  <strong style={{ color: "#16a34a" }}>{smartVideos.filter(v => v.status === "ok").length} nových</strong> · {" "}
                  <span style={{ color: "#666" }}>{smartVideos.filter(v => v.status === "exists").length} v databázi</span> · {" "}
                  <span style={{ color: "#dc2626" }}>{smartVideos.filter(v => v.status === "error").length} chyb</span>
                  {" "} (celkem {smartVideos.length})
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', alignItems: 'center' }}>
                  <input 
                    type="text" 
                    placeholder="Text k odstranění z názvů (např. 'Official Video')" 
                    value={smartRemoveStr}
                    onChange={(e) => setSmartRemoveStr(e.target.value)}
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <button 
                    onClick={applySmartRemoveString}
                    style={{ ...styles.primaryBtn, width: 'auto', marginTop: 0, padding: '0.7rem 1rem' }}
                    disabled={!smartRemoveStr}
                  >
                    Odstranit
                  </button>
                </div>
                
                <div style={{ ...styles.importList, maxHeight: 500 }}>
                  {smartVideos.map((video, index) => {
                    const isOk = video.status === "ok";
                    const isExists = video.status === "exists";
                    const isError = video.status === "error";
                    
                    let bg = "#fafafa";
                    if (isExists) bg = "#f0fdf4"; // light green
                    else if (isError) bg = "#fef2f2"; // light red
                    
                    return (
                      <div key={index} style={{ ...styles.importItem, background: bg }}>
                        <div style={styles.importItemMain}>
                          {isOk ? (
                            <input
                              type="text"
                              value={video.title}
                              onChange={(e) => updateSmartVideoTitle(index, e.target.value)}
                              style={{ ...styles.importTitleInput, fontWeight: 600 }}
                            />
                          ) : (
                            <div style={{ fontWeight: 600, fontSize: "0.85rem", color: isExists ? "#16a34a" : "#dc2626" }}>
                              {video.title}
                            </div>
                          )}
                          <div style={styles.importVideoId}>
                            ID: {video.videoId} {video.channelName && `· Kanál: ${video.channelName}`}
                          </div>
                        </div>
                        
                        {isOk && (
                          <select 
                            style={{ ...styles.input, width: "160px", padding: "0.4rem", fontSize: "0.8rem", marginLeft: "10px" }}
                            value={video.selectedArtistId || ""}
                            onChange={(e) => updateSmartVideoArtist(index, e.target.value)}
                          >
                            <option value="">Zvolte interpreta...</option>
                            {artists.map(a => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                          </select>
                        )}
                        {isExists && <span style={{ fontSize: "1.2rem", padding: "0 10px" }}>✅</span>}
                        {isError && <span style={{ fontSize: "1.2rem", padding: "0 10px" }}>❌</span>}
                        
                        <button
                          onClick={() => removeSmartVideo(index)}
                          style={styles.importDeleteBtn}
                          title="Odstranit z listu"
                        >
                          🗑
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={generateSmartSql}
                  style={{
                    ...styles.primaryBtn,
                    marginTop: "1.5rem",
                  }}
                  disabled={!smartVideos.some(v => v.status === "ok" && v.selectedArtistId)}
                >
                  Generovat SQL pro nová videa
                </button>

                {smartSqlOutput && (
                  <div style={styles.sqlOutputCard}>
                    <pre style={styles.sqlCode}>{smartSqlOutput}</pre>
                    <div style={styles.sqlActions}>
                      <button onClick={copySmartSql} style={styles.copyBtn}>
                        {smartCopied ? "✓ Zkopírováno!" : "📋 Kopírovat"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Styles ───────────────────────────────────────────── */

const styles = {
  /* Page */
  page: {
    minHeight: "100vh",
    background: "#f5f5f7",
    padding: "2rem 1rem 4rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: "#333",
  },
  container: {
    maxWidth: 800,
    margin: "0 auto",
  },

  /* Header */
  header: {
    textAlign: "center",
    marginBottom: "2rem",
  },
  headerIcon: {
    fontSize: "2.5rem",
    marginBottom: "0.25rem",
  },
  headerTitle: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: "#222",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  headerSub: {
    fontSize: "0.85rem",
    color: "#888",
    marginTop: "0.25rem",
  },

  /* Tabs */
  tabs: {
    display: "flex",
    gap: 4,
    marginBottom: "1.5rem",
    background: "#e8e8ec",
    borderRadius: 12,
    padding: 4,
  },
  tab: {
    flex: 1,
    padding: "0.7rem",
    fontSize: "0.9rem",
    fontWeight: 600,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    background: "transparent",
    color: "#888",
    transition: "all 0.2s ease",
  },
  tabActive: {
    background: "#fff",
    color: "#333",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
  },

  /* Card */
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "1.75rem",
    border: "1px solid #e0e0e0",
  },
  cardDescription: {
    color: "#888",
    fontSize: "0.9rem",
    marginTop: 0,
    marginBottom: "1.25rem",
    lineHeight: 1.5,
  },

  /* Error */
  errorBanner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.75rem 1rem",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    marginBottom: "1rem",
    color: "#dc2626",
    fontSize: "0.9rem",
  },
  errorClose: {
    background: "none",
    border: "none",
    color: "#dc2626",
    cursor: "pointer",
    fontSize: "1rem",
    padding: 0,
    marginLeft: "0.5rem",
  },

  /* Steps */
  step: {
    marginBottom: "1.5rem",
  },
  stepHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: "0.6rem",
  },
  stepNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#ede9fe",
    color: "#6c5ce7",
    fontSize: "0.75rem",
    fontWeight: 700,
  },
  stepLabel: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#444",
  },

  /* Inputs */
  artistGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
    gap: 12,
    maxHeight: 450,
    overflowY: "auto",
    marginTop: "0.5rem",
    padding: "0.25rem",
  },
  artistCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0.5rem 0.25rem",
    borderRadius: 10,
    border: "1px solid #e0e0e0",
    background: "#fafafa",
    cursor: "pointer",
    transition: "all 0.15s ease",
    overflow: "hidden",
  },
  artistCardSelected: {
    border: "2px solid #6c5ce7",
    background: "#f3f0ff",
  },
  artistImg: {
    width: 56,
    height: 56,
    borderRadius: 8,
    objectFit: "cover",
    marginBottom: "0.3rem",
  },
  artistName: {
    fontSize: "0.72rem",
    textAlign: "center",
    color: "#555",
    lineHeight: 1.2,
    wordBreak: "break-word",
  },
  input: {
    width: "100%",
    padding: "0.7rem 0.9rem",
    borderRadius: 10,
    border: "1px solid #ddd",
    background: "#fff",
    color: "#333",
    fontSize: "0.95rem",
    outline: "none",
    boxSizing: "border-box",
  },
  badge: {
    display: "inline-block",
    marginTop: "0.4rem",
    padding: "0.2rem 0.6rem",
    borderRadius: 6,
    background: "#ede9fe",
    color: "#6c5ce7",
    fontSize: "0.78rem",
    fontFamily: "monospace",
  },
  loadingPulse: {
    padding: "0.7rem",
    color: "#888",
    fontStyle: "italic",
    fontSize: "0.9rem",
  },

  /* Buttons */
  primaryBtn: {
    width: "100%",
    padding: "0.85rem",
    fontSize: "0.95rem",
    fontWeight: 600,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    background: "#6c5ce7",
    color: "#fff",
    transition: "all 0.2s ease",
    marginTop: "0.25rem",
  },
  primaryBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },

  /* SQL Output */
  sqlOutputCard: {
    marginTop: "1.25rem",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e0e0e0",
  },
  sqlCode: {
    background: "#1e1e2e",
    color: "#a5f3c4",
    padding: "1rem",
    margin: 0,
    fontSize: "0.82rem",
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    lineHeight: 1.6,
  },
  sqlActions: {
    display: "flex",
    gap: 0,
  },
  copyBtn: {
    flex: 1,
    padding: "0.65rem",
    border: "none",
    background: "#28a745",
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.2s",
  },
  resetBtn: {
    padding: "0.65rem 1.2rem",
    border: "none",
    background: "#f0f0f0",
    color: "#666",
    fontSize: "0.85rem",
    cursor: "pointer",
    transition: "background 0.2s",
  },

  /* Progress */
  progressSection: {
    marginTop: "1.25rem",
  },
  progressTrack: {
    height: 6,
    background: "#e8e8ec",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "#6c5ce7",
    borderRadius: 3,
    transition: "width 0.3s ease",
  },
  progressText: {
    marginTop: "0.5rem",
    fontSize: "0.8rem",
    color: "#888",
    textAlign: "center",
  },

  /* Results */
  results: {
    marginTop: "1.5rem",
  },
  successBanner: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "1rem 1.25rem",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 12,
    fontSize: "0.9rem",
    color: "#16a34a",
  },
  successIcon: {
    fontSize: "1.5rem",
  },
  warningBanner: {
    padding: "0.75rem 1rem",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 10,
    color: "#b45309",
    fontSize: "0.9rem",
    marginBottom: "1rem",
  },
  brokenList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  brokenCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.85rem 1rem",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    gap: 12,
  },
  brokenMain: {
    flex: 1,
    minWidth: 0,
  },
  brokenArtist: {
    fontWeight: 600,
    fontSize: "0.88rem",
    color: "#333",
  },
  brokenTitle: {
    fontSize: "0.82rem",
    color: "#666",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  brokenVideoId: {
    fontSize: "0.72rem",
    color: "#999",
    fontFamily: "monospace",
    marginTop: 2,
  },
  brokenActions: {
    display: "flex",
    gap: 6,
    flexShrink: 0,
  },
  openLink: {
    padding: "0.4rem 0.7rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    background: "#f0f0f0",
    color: "#6c5ce7",
    textDecoration: "none",
    borderRadius: 6,
    transition: "background 0.2s",
  },
  deleteCopyBtn: {
    padding: "0.4rem 0.7rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    borderRadius: 6,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background 0.2s",
  },

  /* Import */
  importCount: {
    fontSize: "0.85rem",
    color: "#888",
    marginBottom: "0.75rem",
    fontWeight: 600,
  },
  importList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 400,
    overflowY: "auto",
  },
  importItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0.6rem 0.75rem",
    background: "#fafafa",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
  },
  importItemMain: {
    flex: 1,
    minWidth: 0,
  },
  importTitleInput: {
    width: "100%",
    padding: "0.35rem 0.5rem",
    border: "1px solid #ddd",
    borderRadius: 6,
    fontSize: "0.85rem",
    color: "#333",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },
  importVideoId: {
    fontSize: "0.7rem",
    color: "#999",
    fontFamily: "monospace",
    marginTop: 2,
  },
  importDeleteBtn: {
    background: "none",
    border: "none",
    fontSize: "1.1rem",
    cursor: "pointer",
    padding: "0.25rem",
    opacity: 0.5,
    transition: "opacity 0.15s",
  },
};

export default SqlGenerator;
